import { useCallback, useMemo, useReducer, useRef, type ReactElement, type ReactNode } from 'react';
import { HeaderOverlay } from './header-overlay';
import { HeaderPanelContext, type HeaderPanelId, type HeaderPanelContextValue } from './header-panel-context';

type HeaderPanelState = {
    searchOpen: boolean;
    desktopMenuOpen: boolean;
};

type HeaderPanelAction = {
    type: 'SET_PANEL_OPEN';
    panel: HeaderPanelId;
    open: boolean;
};

function headerPanelReducer(state: HeaderPanelState, action: HeaderPanelAction): HeaderPanelState {
    if (action.type !== 'SET_PANEL_OPEN') {
        return state;
    }

    if (action.panel === 'search') {
        return {
            searchOpen: action.open,
            desktopMenuOpen: action.open ? false : state.desktopMenuOpen,
        };
    }

    return {
        searchOpen: action.open ? false : state.searchOpen,
        desktopMenuOpen: action.open,
    };
}

/**
 * Coordinates search ↔ desktop-nav exclusivity and owns the single shared header backdrop.
 *
 * Backdrop covers: search (mobile + desktop) and desktop mega-menu only.
 * Mobile nav Sheet already provides its own full-screen overlay — do not drive this backdrop from it.
 */
export function HeaderPanelProvider({ children }: { children: ReactNode }): ReactElement {
    const [state, dispatch] = useReducer(headerPanelReducer, { searchOpen: false, desktopMenuOpen: false });
    const closeHandlersRef = useRef<Partial<Record<HeaderPanelId, (() => void) | null>>>({});

    const registerPanelCloseHandler = useCallback((panel: HeaderPanelId, handler: (() => void) | null) => {
        closeHandlersRef.current[panel] = handler;
    }, []);

    const setPanelOpen = useCallback((panel: HeaderPanelId, open: boolean) => {
        dispatch({ type: 'SET_PANEL_OPEN', panel, open });
        if (open) {
            const otherPanel: HeaderPanelId = panel === 'search' ? 'menu' : 'search';
            closeHandlersRef.current[otherPanel]?.();
        }
    }, []);

    const closeActivePanel = useCallback(() => {
        if (state.searchOpen) {
            closeHandlersRef.current.search?.();
            return;
        }
        if (state.desktopMenuOpen) {
            closeHandlersRef.current.menu?.();
        }
    }, [state.searchOpen, state.desktopMenuOpen]);

    const value = useMemo<HeaderPanelContextValue>(
        () => ({
            isSearchSuggestionsOpen: state.searchOpen,
            setPanelOpen,
            registerPanelCloseHandler,
        }),
        [state.searchOpen, setPanelOpen, registerPanelCloseHandler]
    );

    const isBackdropOpen = state.searchOpen || state.desktopMenuOpen;

    return (
        <HeaderPanelContext.Provider value={value}>
            <HeaderOverlay open={isBackdropOpen} onClose={closeActivePanel} />
            {children}
        </HeaderPanelContext.Provider>
    );
}
